import { AgGridCellTooltipComponent, AgGridCellTooltipValueGetter } from 'components/AgGrid/AgGridCellTooltipComponent';
import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { getColumnId, getColumnsWithPinnedProp, onTableSettingsConfirm } from 'components/TableSettings';
import type { BimWorkOrderChangeOtherPriceData, WorkOrderChangeStatusEnum } from 'gql/graphql';
import { DeviationEnum } from 'gql/graphql';
import { usePin } from 'hooks/usePin';
import { useSelection } from 'hooks/useSelection';
import {
    bimNumberFormatter,
    percentCellFormatter,
    statusRenderer,
} from 'pages/Project/BimProject/BimCommonComponents/WorkOrderPriceCellRenderers/WorkOrderOtherCellRenderers';
import { ActsTotalHeaderComponent } from 'pages/Project/BimProject/WorkOrder/BimWorkOrderOthersPrice/components/ActsTotalHeaderComponent';
import { cellWithDifferentFiled } from 'pages/Project/BimProject/WorkOrder/BimWorkOrderPriceTable/cellRenderers';
import { formatNumberToRu } from 'utils';

import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';

import { bimWorkOrderChangeOtherNameFilterGql } from '../bimWorkOrderChangeOthersQueries';
import type { TGroupedTableItem } from '../components';

import { useWorkOrderChangeOtherMenuColumn } from './useWorkOrderChangeOtherMenuColumn';

const rightAlignedCellClassName = 'ag-right-aligned-cell';
const rightAlignedBoldCellClassName = 'ag-right-aligned-cell boldWeight';

type TValueKeys = 'planValue' | 'completedValue' | 'remainderValue';

type TWorkOrderChangeOtherColumns = {
    workOrderChangeId: number;
    workOrderRootObjectId: number | undefined;
    workOrderChangeRootObjectId: number | undefined;
    constructionTypeId: number | undefined;
    changeFilterHandler: ({ fieldName, checkedValues }: { fieldName: string; checkedValues: string[] }) => void;
    hasExcessCompletedOthers: boolean;
    refreshTable: () => void;
    workOrderChangeStatus: WorkOrderChangeStatusEnum | undefined;
};

export const totalLeftSide = (data: BimWorkOrderChangeOtherPriceData) => {
    if (data?.completedOtherDeviation === DeviationEnum.Up) {
        return <span style={{ color: 'var(--color-typo-alert)', marginRight: 5 }}>▲</span>;
    }

    return undefined;
};

const getValueField = ({
    valueKey,
    hasExcessCompletedOthers = false,
    totalLeftSideGetter = undefined,
    cellClass = rightAlignedCellClassName,
}: {
    valueKey: TValueKeys;
    hasExcessCompletedOthers?: boolean;
    totalLeftSideGetter?: (data: BimWorkOrderChangeOtherPriceData) => JSX.Element | undefined;
    cellClass?: string;
}): ColDef<BimWorkOrderChangeOtherPriceData>[] => {
    const keys = {
        otherValue: `${valueKey}.others`,
        totalValue: `${valueKey}.total`,
    };

    return [
        {
            headerName: 'Прочие затраты',
            field: keys.otherValue as any,
            width: 150,
            valueFormatter: bimNumberFormatter(0),
            cellRenderer: cellWithDifferentFiled(keys.otherValue, keys.otherValue, totalLeftSideGetter),
            cellClass,
            headerComponent: ActsTotalHeaderComponent,
            headerComponentParams: {
                collapseKeys: [],
                isExpanded: true,
                setIsExpanded: () => undefined,
                hasExcessCompletedOthers,
            },
        },
        {
            headerName: 'Всего',
            field: keys.totalValue as any,
            width: 150,
            valueFormatter: bimNumberFormatter(0),
            cellRenderer: cellWithDifferentFiled(keys.totalValue, keys.totalValue),
            cellClass,
        },
    ];
};

export const useWorkOrderChangeOtherColumns = ({
    workOrderChangeId,
    workOrderRootObjectId,
    workOrderChangeRootObjectId,
    changeFilterHandler,
    hasExcessCompletedOthers,
    constructionTypeId,
    refreshTable,
    workOrderChangeStatus,
}: TWorkOrderChangeOtherColumns) => {
    const filterVariables = useMemo(
        () => ({
            workOrderChangeId,
            workOrderRootObjectId,
            workOrderChangeRootObjectId,
        }),
        [workOrderChangeId, workOrderChangeRootObjectId, workOrderRootObjectId],
    );

    const {
        menuColumn,
        checkedRows,
        resetCheckedRows,
        isSingleDeleteModalOpened,
        closeSingleModalHandler,
        isExcludeModalOpened,
        totalDetailsOpened,
        toggleTotalDetails,
    } = useWorkOrderChangeOtherMenuColumn({ constructionTypeId, refreshTable, workOrderChangeStatus });

    const columns = useMemo<(ColDef<TGroupedTableItem> | ColGroupDef<TGroupedTableItem>)[]>(
        () => [
            menuColumn,
            {
                headerName: 'Шифр',
                field: 'valueCode' as any,
                width: 240,
                lockPosition: 'left',
                cellRenderer: statusRenderer,
            },
            {
                headerName: 'Наименование работ, затрат',
                field: 'name',
                flex: 1,
                minWidth: 400,
                lockPosition: 'left',
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    variables: filterVariables,
                    changeFilterHandler,
                    dataAccessor: 'bimWorkOrderChangeOtherNameFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Наименование работ, затрат',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: bimWorkOrderChangeOtherNameFilterGql,
                },
            },
            {
                headerName: 'Кол-во',
                field: 'calculatingTypeValue',
                width: 140,
                valueFormatter: percentCellFormatter,
            },
            {
                headerName: 'План по НЗ',
                marryChildren: true,
                children: getValueField({ valueKey: 'planValue' }),
            },
            {
                headerName: 'Выполнено по НЗ',
                marryChildren: true,
                children: getValueField({
                    valueKey: 'completedValue',
                    hasExcessCompletedOthers,
                    totalLeftSideGetter: totalLeftSide,
                }),
            },
            {
                headerName: 'Остаток по НЗ',
                marryChildren: true,
                children: getValueField({ valueKey: 'remainderValue' }),
            },
            {
                headerName: 'Обоснование',
                field: 'rationale',
                minWidth: 180,
                tooltipComponent: AgGridCellTooltipComponent,
                tooltipValueGetter: AgGridCellTooltipValueGetter({}),
            },
        ],
        [changeFilterHandler, filterVariables, hasExcessCompletedOthers, menuColumn],
    );

    const detailsColumns = useMemo<ColDef[]>(
        () => [
            {
                headerName: 'Наименование работ, затрат',
                field: 'name',
                flex: 1,
                minWidth: 250,
                spanHeaderHeight: true,
                lockPosition: 'left',
            },
            {
                headerName: 'План по НЗ',
                marryChildren: true,
                children: getValueField({
                    valueKey: 'planValue',
                    cellClass: rightAlignedBoldCellClassName,
                }),
            },
            {
                headerName: 'Выполнено по НЗ',
                field: 'completedValue.total',
                width: 200,
                spanHeaderHeight: true,
                cellClass: rightAlignedBoldCellClassName,
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 0) || '',
            },
            {
                headerName: 'Остаток по НЗ',
                field: 'remainderValue.total',
                width: 200,
                spanHeaderHeight: true,
                cellClass: rightAlignedBoldCellClassName,
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 0) || '',
            },
        ],
        [],
    );
    const [selection, , , setSelection] = useSelection(`bimWorkOrderChangeOtherTable_${workOrderChangeId}`);
    const [pinnedItems, , , setPinned] = usePin(`bimWorkOrderChangeOtherTable_${workOrderChangeId}`);

    const isColumnVisible = useCallback((id: string) => selection[id] ?? true, [selection]);
    const fullSetSettingsColumns = useMemo(
        () =>
            columns.reduce((acc: ColDef[], column, idx) => {
                if (idx > 0) {
                    const newColumn = { ...column } as ColDef & { disabled: boolean };

                    if (idx < 3) {
                        newColumn.disabled = true;
                    }

                    if (pinnedItems[getColumnId(column)]) {
                        newColumn.pinned = true;
                    }

                    acc.push(newColumn);

                    return acc;
                }

                return acc;
            }, []),
        [columns, pinnedItems],
    );

    const tableSettingsConfirm = useCallback(
        (visible) => {
            onTableSettingsConfirm({
                settingColumns: fullSetSettingsColumns,
                incomingColumns: visible,
                setSelection,
                setPinned,
            });
        },
        [fullSetSettingsColumns, setPinned, setSelection],
    );
    const fullSetColumns = useMemo(() => {
        const fullSetColumnsWithPins = getColumnsWithPinnedProp({ columns, pinnedItems });

        return fullSetColumnsWithPins.filter((column) => isColumnVisible(getColumnId(column)));
    }, [columns, isColumnVisible, pinnedItems]);

    return {
        fullSetColumns,
        checkedRows,
        resetCheckedRows,
        isSingleDeleteModalOpened,
        closeSingleModalHandler,
        isExcludeModalOpened,
        totalDetailsOpened,
        closeTotalDetails: toggleTotalDetails,
        detailsColumns,
        fullSetSettingsColumns,
        isColumnVisible,
        tableSettingsConfirm,
    };
};
